import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { Home } from '@home-guard/shared';

const schema = z.object({
  address_line1: z.string().min(5, 'Enter your street address').max(200).trim(),
  city: z.string().min(2, 'Enter your city').max(100).trim(),
  state: z.string().length(2, 'Use 2-letter state code (e.g. TX)').toUpperCase(),
  zip_code: z.string().regex(/^\d{5}(-\d{4})?$/, 'Enter a valid ZIP code'),
  year_built: z.preprocess(
    (v) => (v === '' || v === undefined ? null : Number(v)),
    z.number().int()
      .min(1800, `Please enter a valid year between 1800 and ${new Date().getFullYear()}`)
      .max(new Date().getFullYear(), `Please enter a valid year between 1800 and ${new Date().getFullYear()}`)
      .nullable(),
  ),
  structure_type: z.enum(['single_family', 'townhouse', 'condo', 'mobile_home', '']).transform(
    (v) => (v === '' ? null : v),
  ).nullable(),
  has_well:   z.boolean().optional(),
  has_septic: z.boolean().optional(),
  is_rural:   z.boolean().optional(),
  // Plumbing
  pipe_material: z.enum(['copper','cpvc','pex','galvanized','cast_iron','pvc','mixed','unknown','']).transform(
    (v) => (v === '' ? null : v),
  ).nullable().optional(),
  pipe_age_years: z.preprocess(
    (v) => (v === '' || v === undefined ? null : Number(v)),
    z.number().int().min(0).max(200).nullable(),
  ).optional(),
  has_sump_pump: z.boolean().optional(),
  // Windows
  window_type: z.enum(['single_pane','double_pane','triple_pane','unknown','']).transform(
    (v) => (v === '' ? null : v),
  ).nullable().optional(),
  window_material: z.enum(['vinyl','wood','aluminum','fiberglass','composite','']).transform(
    (v) => (v === '' ? null : v),
  ).nullable().optional(),
  // Garage
  has_garage: z.boolean().optional(),
  garage_door_count: z.preprocess(
    (v) => (v === '' || v === undefined ? null : Number(v)),
    z.number().int().min(1).max(10).nullable(),
  ).optional(),
  // Outdoor
  has_deck: z.boolean().optional(),
  deck_material: z.enum(['wood','composite','concrete','pvc','aluminum','']).transform(
    (v) => (v === '' ? null : v),
  ).nullable().optional(),
  driveway_material: z.enum(['concrete','asphalt','gravel','paver','dirt','']).transform(
    (v) => (v === '' ? null : v),
  ).nullable().optional(),
  has_fence: z.boolean().optional(),
  fence_material: z.enum(['wood','vinyl','metal','chain_link','composite','']).transform(
    (v) => (v === '' ? null : v),
  ).nullable().optional(),
  // Modern features
  has_smart_home: z.boolean().optional(),
  has_ev_charger: z.boolean().optional(),
  ev_charger_level: z.enum(['level1','level2','level3','']).transform(
    (v) => (v === '' ? null : v),
  ).nullable().optional(),
});

type FormData = z.infer<typeof schema>;

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '12px 16px',
  borderRadius: 12,
  border: '1.5px solid #d1d5db',
  fontSize: 16,
  outline: 'none',
  boxSizing: 'border-box',
  background: '#fff',
};

const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: 14, fontWeight: 500,
  color: '#374151', marginBottom: 6,
};

const sectionStyle: React.CSSProperties = {
  background: '#fff', borderRadius: 12,
  border: '1.5px solid #d1d5db', overflow: 'hidden',
};

const sectionHeaderStyle: React.CSSProperties = {
  margin: 0, padding: '12px 16px',
  fontSize: 13, fontWeight: 600, color: '#6b7280',
  borderBottom: '1px solid #f3f4f6', letterSpacing: '0.05em',
  textTransform: 'uppercase',
};

const infoBoxStyle: React.CSSProperties = {
  background: '#f0f9ff', borderRadius: 8, padding: '8px 12px',
  fontSize: 12, color: '#0369a1', marginTop: 6, lineHeight: 1.5,
};

function Toggle({
  name,
  checked,
  label,
  hint,
  onChange,
}: {
  name: string;
  checked: boolean;
  label: string;
  hint: string;
  onChange: (v: boolean) => void;
}) {
  return (
    <label
      htmlFor={name}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '14px 16px', cursor: 'pointer',
        borderBottom: '1px solid #f3f4f6',
      }}
    >
      <div>
        <p style={{ margin: 0, fontSize: 15, color: '#111827', fontWeight: 500 }}>{label}</p>
        <p style={{ margin: '2px 0 0', fontSize: 12, color: '#9ca3af' }}>{hint}</p>
      </div>
      <div style={{ position: 'relative', flexShrink: 0, marginLeft: 12 }}>
        <input
          id={name}
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          style={{
            opacity: 0, width: 44, height: 24, position: 'absolute',
            top: 0, left: 0, cursor: 'pointer', margin: 0, zIndex: 1,
          }}
        />
        <div style={{
          width: 44, height: 24, borderRadius: 12,
          background: checked ? '#1D4ED8' : '#d1d5db',
          transition: 'background 0.15s', pointerEvents: 'none',
        }}>
          <div style={{
            position: 'absolute', top: 2,
            left: checked ? 22 : 2,
            width: 20, height: 20, borderRadius: 10,
            background: '#fff',
            boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
            transition: 'left 0.15s',
          }} />
        </div>
      </div>
    </label>
  );
}

export function HomeSetupPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({ resolver: zodResolver(schema) });

  const hasWell       = watch('has_well', false);
  const hasSeptic     = watch('has_septic', false);
  const isRural       = watch('is_rural', false);
  const hasSumpPump   = watch('has_sump_pump', false);
  const hasGarage     = watch('has_garage', false);
  const hasDeck       = watch('has_deck', false);
  const hasFence      = watch('has_fence', false);
  const hasSmartHome  = watch('has_smart_home', false);
  const hasEvCharger  = watch('has_ev_charger', false);
  const pipeMaterial  = watch('pipe_material');
  const windowType    = watch('window_type');

  const onSubmit = async (data: FormData) => {
    try {
      await api.post<Home>('/homes', {
        address_line1: data.address_line1,
        city: data.city,
        state: data.state,
        zip_code: data.zip_code,
        ...(data.year_built !== null ? { year_built: data.year_built } : {}),
        ...(data.structure_type !== null ? { structure_type: data.structure_type } : {}),
        has_well:   data.has_well   ?? false,
        has_septic: data.has_septic ?? false,
        is_rural:   data.is_rural   ?? false,
        // Plumbing
        ...(data.pipe_material    ? { pipe_material: data.pipe_material }       : {}),
        ...(data.pipe_age_years   ? { pipe_age_years: data.pipe_age_years }     : {}),
        has_sump_pump: data.has_sump_pump ?? false,
        // Windows
        ...(data.window_type     ? { window_type: data.window_type }           : {}),
        ...(data.window_material ? { window_material: data.window_material }   : {}),
        // Garage
        has_garage: data.has_garage ?? false,
        ...(data.garage_door_count ? { garage_door_count: data.garage_door_count } : {}),
        // Outdoor
        has_deck: data.has_deck ?? false,
        ...(data.deck_material    ? { deck_material: data.deck_material }       : {}),
        ...(data.driveway_material ? { driveway_material: data.driveway_material } : {}),
        has_fence: data.has_fence ?? false,
        ...(data.fence_material   ? { fence_material: data.fence_material }     : {}),
        // Modern
        has_smart_home: data.has_smart_home ?? false,
        has_ev_charger: data.has_ev_charger ?? false,
        ...(data.ev_charger_level ? { ev_charger_level: data.ev_charger_level } : {}),
        is_primary: true,
      });
      await queryClient.invalidateQueries({ queryKey: ['homes'] });
      navigate('/app/dashboard', { replace: true });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to save home. Please try again.';
      setError('root', { message: msg });
    }
  };

  return (
    <div style={{ minHeight: '100dvh', background: '#f9fafb', padding: '32px 24px 60px' }}>
      <div style={{ maxWidth: 480, margin: '0 auto' }}>
        <div style={{ marginBottom: 32 }}>
          <h1 style={{ fontSize: 26, fontWeight: 700, color: '#111827', margin: '0 0 8px' }}>
            Tell us about your home
          </h1>
          <p style={{ color: '#6b7280', fontSize: 15, margin: 0 }}>
            We&apos;ll use this to generate a personalized maintenance schedule.
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Address */}
          <div>
            <label style={labelStyle}>
              Street address <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              {...register('address_line1')}
              type="text"
              autoComplete="address-line1"
              placeholder="123 Main St"
              style={inputStyle}
            />
            {errors.address_line1 && (
              <p style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }}>{errors.address_line1.message}</p>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 120px', gap: 12 }}>
            <div>
              <label style={labelStyle}>City <span style={{ color: '#ef4444' }}>*</span></label>
              <input {...register('city')} type="text" autoComplete="address-level2" placeholder="Austin" style={inputStyle} />
              {errors.city && <p style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }}>{errors.city.message}</p>}
            </div>
            <div>
              <label style={labelStyle}>State <span style={{ color: '#ef4444' }}>*</span></label>
              <input {...register('state')} type="text" autoComplete="address-level1" placeholder="TX" maxLength={2} style={{ ...inputStyle, textTransform: 'uppercase' }} />
              {errors.state && <p style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }}>{errors.state.message}</p>}
            </div>
            <div>
              <label style={labelStyle}>ZIP <span style={{ color: '#ef4444' }}>*</span></label>
              <input {...register('zip_code')} type="text" autoComplete="postal-code" placeholder="78701" style={inputStyle} />
              {errors.zip_code && <p style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }}>{errors.zip_code.message}</p>}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>Year built</label>
              <input {...register('year_built')} type="number" placeholder="e.g. 1998" min={1800} max={new Date().getFullYear()} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Home type</label>
              <select {...register('structure_type')} style={inputStyle}>
                <option value="">Select…</option>
                <option value="single_family">Single family</option>
                <option value="townhouse">Townhouse</option>
                <option value="condo">Condo</option>
                <option value="mobile_home">Mobile home</option>
              </select>
            </div>
          </div>

          {/* Property features */}
          <div style={sectionStyle}>
            <p style={sectionHeaderStyle}>Property features</p>
            <Toggle name="has_well"   checked={hasWell ?? false}   label="Private well water"  hint="Home uses a private well instead of city water" onChange={(v) => setValue('has_well', v)} />
            <Toggle name="has_septic" checked={hasSeptic ?? false} label="Septic system"        hint="Home has a septic tank instead of public sewer" onChange={(v) => setValue('has_septic', v)} />
            <Toggle name="is_rural"   checked={isRural ?? false}   label="Rural property"       hint="Located outside a city or suburban area" onChange={(v) => setValue('is_rural', v)} />
            <Toggle name="has_sump_pump" checked={hasSumpPump ?? false} label="Sump pump in basement" hint="Has a pump to remove water from the basement" onChange={(v) => setValue('has_sump_pump', v)} />
          </div>

          {/* Plumbing */}
          <div style={sectionStyle}>
            <p style={sectionHeaderStyle}>Plumbing</p>
            <div style={{ padding: '14px 16px', borderBottom: '1px solid #f3f4f6' }}>
              <label style={labelStyle}>Pipe material</label>
              <select {...register('pipe_material')} style={inputStyle}>
                <option value="">Don&apos;t know / not sure</option>
                <option value="copper">Copper — gold standard, 50-70 yr lifespan</option>
                <option value="pex">PEX — flexible plastic, modern standard</option>
                <option value="cpvc">CPVC — rigid plastic, brittles with age</option>
                <option value="galvanized">Galvanized steel — older homes, corrodes inside</option>
                <option value="cast_iron">Cast iron — drain pipes in older homes</option>
                <option value="pvc">PVC — drain/vent pipes, never pressure supply</option>
                <option value="mixed">Mixed — multiple materials in same home</option>
                <option value="unknown">Unknown</option>
              </select>
              {pipeMaterial === 'galvanized' && (
                <div style={infoBoxStyle}>
                  Galvanized pipes corrode from the inside out and can contaminate water with lead. We&apos;ll add a water test reminder and re-pipe evaluation to your task list.
                </div>
              )}
              {pipeMaterial === 'cpvc' && (
                <div style={infoBoxStyle}>
                  CPVC becomes brittle after 25+ years. We&apos;ll add inspection reminders based on your pipe age.
                </div>
              )}
            </div>
            <div style={{ padding: '14px 16px', borderBottom: '1px solid #f3f4f6' }}>
              <label style={labelStyle}>Pipe age (years, if known)</label>
              <input {...register('pipe_age_years')} type="number" placeholder="e.g. 15" min={0} max={200} style={inputStyle} />
              <p style={{ margin: '4px 0 0', fontSize: 12, color: '#9ca3af' }}>Leave blank to use your home&apos;s year built</p>
            </div>
          </div>

          {/* Windows */}
          <div style={sectionStyle}>
            <p style={sectionHeaderStyle}>Windows</p>
            <div style={{ padding: '14px 16px', borderBottom: '1px solid #f3f4f6' }}>
              <label style={labelStyle}>Window type</label>
              <select {...register('window_type')} style={inputStyle}>
                <option value="">Don&apos;t know</option>
                <option value="single_pane">Single pane — older homes, poor insulation</option>
                <option value="double_pane">Double pane — standard, argon-filled insulating unit</option>
                <option value="triple_pane">Triple pane — premium, coldest climates</option>
                <option value="unknown">Unknown</option>
              </select>
              {windowType === 'single_pane' && (
                <div style={infoBoxStyle}>
                  Single-pane windows lose 5-10× more heat than double-pane. We&apos;ll add weatherstripping and upgrade tasks.
                </div>
              )}
              {(windowType === 'double_pane' || windowType === 'triple_pane') && (
                <div style={infoBoxStyle}>
                  We&apos;ll add a fog-test reminder to catch seal failures before they waste energy.
                </div>
              )}
            </div>
            <div style={{ padding: '14px 16px' }}>
              <label style={labelStyle}>Window frame material</label>
              <select {...register('window_material')} style={inputStyle}>
                <option value="">Don&apos;t know</option>
                <option value="vinyl">Vinyl — most common, low maintenance</option>
                <option value="wood">Wood — classic look, requires repainting</option>
                <option value="aluminum">Aluminum — durable but conducts heat/cold</option>
                <option value="fiberglass">Fiberglass — premium, most durable</option>
                <option value="composite">Composite — wood/vinyl hybrid</option>
              </select>
            </div>
          </div>

          {/* Garage */}
          <div style={sectionStyle}>
            <p style={sectionHeaderStyle}>Garage</p>
            <Toggle name="has_garage" checked={hasGarage ?? false} label="Has attached or detached garage" hint="Includes carport or outbuilding with a vehicle door" onChange={(v) => setValue('has_garage', v)} />
            {hasGarage && (
              <div style={{ padding: '14px 16px', borderTop: '1px solid #f3f4f6' }}>
                <label style={labelStyle}>Number of garage doors</label>
                <select {...register('garage_door_count')} style={inputStyle}>
                  <option value="">Select…</option>
                  <option value="1">1 door</option>
                  <option value="2">2 doors</option>
                  <option value="3">3 doors</option>
                  <option value="4">4 doors</option>
                </select>
              </div>
            )}
          </div>

          {/* Outdoor features */}
          <div style={sectionStyle}>
            <p style={sectionHeaderStyle}>Outdoor features</p>
            <Toggle name="has_deck" checked={hasDeck ?? false} label="Has deck or porch" hint="Any exterior elevated platform attached to the home" onChange={(v) => setValue('has_deck', v)} />
            {hasDeck && (
              <div style={{ padding: '14px 16px', borderTop: '1px solid #f3f4f6', borderBottom: '1px solid #f3f4f6' }}>
                <label style={labelStyle}>Deck material</label>
                <select {...register('deck_material')} style={inputStyle}>
                  <option value="">Don&apos;t know</option>
                  <option value="wood">Wood — natural, needs sealing every 2-3 yrs</option>
                  <option value="composite">Composite — low maintenance, long lasting</option>
                  <option value="concrete">Concrete — very durable, crack-prone</option>
                  <option value="pvc">PVC — synthetic, no rot or splinters</option>
                  <option value="aluminum">Aluminum — commercial grade, rust-free</option>
                </select>
              </div>
            )}
            <div style={{ padding: '14px 16px', borderBottom: '1px solid #f3f4f6' }}>
              <label style={labelStyle}>Driveway material</label>
              <select {...register('driveway_material')} style={inputStyle}>
                <option value="">No driveway or don&apos;t know</option>
                <option value="concrete">Concrete — seal every 3-5 yrs</option>
                <option value="asphalt">Asphalt — sealcoat every 3-5 yrs</option>
                <option value="gravel">Gravel — rake and regrade periodically</option>
                <option value="paver">Paver — refill joint sand every 2-3 yrs</option>
                <option value="dirt">Dirt / graded earth</option>
              </select>
            </div>
            <Toggle name="has_fence" checked={hasFence ?? false} label="Has fence" hint="Any perimeter fencing around the property" onChange={(v) => setValue('has_fence', v)} />
            {hasFence && (
              <div style={{ padding: '14px 16px', borderTop: '1px solid #f3f4f6' }}>
                <label style={labelStyle}>Fence material</label>
                <select {...register('fence_material')} style={inputStyle}>
                  <option value="">Don&apos;t know</option>
                  <option value="wood">Wood — needs staining every 2-3 yrs</option>
                  <option value="vinyl">Vinyl — low maintenance, 20-30 yr lifespan</option>
                  <option value="metal">Metal (wrought iron / steel) — paint to prevent rust</option>
                  <option value="chain_link">Chain link — galvanized, very low maintenance</option>
                  <option value="composite">Composite — wood/plastic blend</option>
                </select>
              </div>
            )}
          </div>

          {/* Modern systems */}
          <div style={sectionStyle}>
            <p style={sectionHeaderStyle}>Modern systems</p>
            <Toggle name="has_smart_home" checked={hasSmartHome ?? false} label="Smart home devices" hint="Smart lock, thermostat, doorbell camera, or hub" onChange={(v) => setValue('has_smart_home', v)} />
            <Toggle name="has_ev_charger" checked={hasEvCharger ?? false} label="EV charger" hint="Electric vehicle charging station at the home" onChange={(v) => setValue('has_ev_charger', v)} />
            {hasEvCharger && (
              <div style={{ padding: '14px 16px', borderTop: '1px solid #f3f4f6' }}>
                <label style={labelStyle}>Charger level</label>
                <select {...register('ev_charger_level')} style={inputStyle}>
                  <option value="">Don&apos;t know</option>
                  <option value="level1">Level 1 — standard 120V outlet, 3-5 mph charge</option>
                  <option value="level2">Level 2 — 240V dedicated circuit, 15-30 mph charge</option>
                  <option value="level3">Level 3 / DC fast charge — commercial grade</option>
                </select>
                {hasEvCharger && (
                  <div style={infoBoxStyle}>
                    Level 2 chargers draw 32-48A continuously. We&apos;ll add an annual circuit inspection reminder.
                  </div>
                )}
              </div>
            )}
          </div>

          {errors.root && (
            <div style={{
              background: '#fef2f2', border: '1px solid #fecaca',
              borderRadius: 10, padding: '10px 14px',
              color: '#dc2626', fontSize: 14,
            }}>
              {errors.root.message}
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            style={{
              width: '100%', padding: '14px',
              background: isSubmitting ? '#93c5fd' : '#1D4ED8',
              color: '#fff', borderRadius: 12, fontWeight: 600,
              fontSize: 16, border: 'none', cursor: isSubmitting ? 'not-allowed' : 'pointer',
              marginTop: 4,
            }}
          >
            {isSubmitting ? 'Saving…' : 'Save and continue'}
          </button>
        </form>
      </div>
    </div>
  );
}
